import {
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  RedEnvelopeOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Button, Card, Typography, Space, message, Select } from 'antd';
import React from 'react';
import { MainContext } from '../../contexts/mainContext';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { Link } from 'react-router-dom';
import FormItem from '@ant-design/compatible/lib/form/FormItem';
import * as firebase from 'firebase';
import { ALL_DIAL_CODES } from '../../constants/countrycodes';
import RecaptchaVerifier = firebase.auth.RecaptchaVerifier;

interface IFormComponentProps extends FormComponentProps {}

const NormalLoginForm: React.FC<IFormComponentProps> = (props) => {
  const emailRef = React.useRef<FormItem>();
  const recaptchaContainerRef = React.useRef<HTMLDivElement>();
  const [phoneLogin, setPhoneLogin] = React.useState(false);
  const [phonePrefix, setPhonePrefix] = React.useState('+34');
  const [loading, setLoading] = React.useState(false);
  const [verifyPhone, setVerifyPhone] = React.useState<
    firebase.auth.ConfirmationResult | undefined
  >();
  const phoneCaptchaRef = React.useRef<RecaptchaVerifier | undefined>();

  const loginWithPhoneNumber = async (phone: string) => {
    phoneCaptchaRef.current = new firebase.auth.RecaptchaVerifier(
      'recaptcha-verifier',
      {
        size: 'normal',
        callback: (response) => {
          message.loading({
            key: 'sms-message',
            content: 'Enviando codigo SMS',
          });

          firebase
            .auth()
            .signInWithPhoneNumber(
              `${phonePrefix}${phone}`,
              phoneCaptchaRef.current,
            )
            .then((result) => {
              message.success({
                key: 'sms-message',
                content: 'Codigo SMS enviado',
              });
              setVerifyPhone(result);

              phoneCaptchaRef.current.clear();
              recaptchaContainerRef.current.innerHTML = `<span id="recaptcha-verifier"></span>`;
            })
            .catch(() => {
              message.error({
                key: 'sms-message',
                content: 'Ha ocurrido un error al enviar el código SMS',
              });
              phoneCaptchaRef.current
                .render()
                // @ts-ignore
                .then((widgetId) => window.grecaptcha.reset(widgetId));
            });
        },
      },
    );

    await phoneCaptchaRef.current.render();
  };

  const verifySMS = async (code: string) => {
    await verifyPhone?.confirm(code);
  };

  const login = async (values: {
    username?: string;
    password?: string;
    phone?: string;
    code?: string;
  }): Promise<void> => {
    try {
      setLoading(true);
      if (!phoneLogin) {
        await firebase
          .auth()
          .signInWithEmailAndPassword(values.username, values.password);
      } else {
        if (values.phone) {
          await loginWithPhoneNumber(values.phone);
        }
        if (values.code && verifyPhone) {
          await verifySMS(values.code);
        }
      }
    } catch (error) {
      const errorCode = error.code;
      const errorMessage = error.message;
      console.error(errorCode, errorMessage);

      const errors = {
        'auth/user-disabled': 'Acceso denegado',
        'auth/invalid-verification-code':
          'El código de verificación es incorrecto o ha expirado',
        'auth/wrong-password': 'Contraseña incorrecta',
        'auth/user-not-found': 'Este usuario no existe',
      };

      message.error(
        errors[errorCode] || 'Ha ocurrido un error al iniciar sesión',
        6,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    // @ts-ignore
    props.form.validateFields(async (err, values) => {
      if (!err) {
        return login(values);
      }
    });
  };

  const { getFieldDecorator } = props.form;

  const togglePhoneLogin = () => {
    setPhoneLogin(!phoneLogin);
  };

  const handlePrefixChange = (value: string) => {
    setPhonePrefix(value);
  };

  React.useEffect(() => {
    setVerifyPhone(undefined);
    setLoading(false);
  }, [phoneLogin]);

  console.log('loading', loading);

  return (
    <div style={{ background: '#ECECEC', padding: '30px' }}>
      <Card
        title="Bienvenido"
        bordered={false}
        style={{ width: 300, margin: 'auto', marginBottom: 16 }}
      >
        <Form onSubmit={handleSubmit} className="login-form">
          {!phoneLogin && (
            <>
              <Form.Item ref={emailRef}>
                {getFieldDecorator('username', {
                  rules: [
                    {
                      required: true,
                      message: 'Por favor, introduce tu correo electrónico',
                    },
                  ],
                })(
                  <Input
                    prefix={
                      <MailOutlined
                        style={{ color: 'rgba(0,0,0,.25)' }}
                        translate={undefined}
                      />
                    }
                    type="email"
                    placeholder="Correo electrónico"
                  />,
                )}
              </Form.Item>
              <Form.Item>
                {getFieldDecorator('password', {
                  rules: [
                    {
                      required: true,
                      message: 'Por favor, introduce tu contraseña',
                    },
                  ],
                })(
                  <Input
                    prefix={
                      <LockOutlined
                        style={{ color: 'rgba(0,0,0,.25)' }}
                        translate={undefined}
                      />
                    }
                    type="password"
                    placeholder="Contraseña"
                  />,
                )}
              </Form.Item>
            </>
          )}
          {phoneLogin && verifyPhone && (
            <>
              <Typography.Paragraph style={{ marginBottom: '8px' }}>
                Introduce el código que has recibido por SMS
              </Typography.Paragraph>
              <Form.Item>
                {getFieldDecorator('code', {
                  rules: [
                    {
                      required: true,
                      message:
                        'Por favor, introduce el código que has recibido por SMS',
                    },
                  ],
                })(<Input autoFocus required type="text" />)}
              </Form.Item>
            </>
          )}

          {phoneLogin && !verifyPhone && (
            <Form.Item>
              {getFieldDecorator('phone', {
                rules: [
                  {
                    required: true,
                    message: 'Por favor, introduce un número de teléfono',
                  },
                ],
              })(
                <Input
                  autoFocus={true}
                  addonBefore={
                    <Select
                      style={{ width: 80 }}
                      defaultValue={'+34'}
                      showSearch={true}
                      allowClear={false}
                      onChange={handlePrefixChange}
                    >
                      {ALL_DIAL_CODES.map((dialCode) => (
                        <Select.Option key={dialCode} value={dialCode}>
                          {dialCode}
                        </Select.Option>
                      ))}
                    </Select>
                  }
                  prefix={
                    <PhoneOutlined
                      style={{ color: 'rgba(0,0,0,.25)' }}
                      translate={undefined}
                    />
                  }
                  type="tel"
                  placeholder="Número de teléfono"
                />,
              )}
            </Form.Item>
          )}
          <Form.Item>
            {!phoneLogin && (
              <Link
                to={{
                  pathname: '/password-reset',
                }}
                className="login-form-forgot"
              >
                Restablecer contraseña
              </Link>
            )}
            <Button
              type="primary"
              loading={loading}
              htmlType="submit"
              className="login-form-button"
            >
              {phoneLogin
                ? verifyPhone
                  ? 'Continuar'
                  : 'Enviar código SMS'
                : 'Iniciar sesión'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <span ref={recaptchaContainerRef}>
        <span id={'recaptcha-verifier'} />
      </span>
      <Space
        align={'center'}
        style={{ width: '100%', padding: 16 }}
        direction={'vertical'}
      >
        {!phoneLogin && (
          <Button type={'text'} onClick={togglePhoneLogin}>
            Iniciar sesión con número de teléfono
          </Button>
        )}
        {phoneLogin && (
          <Button type={'text'} onClick={togglePhoneLogin}>
            Iniciar sesión con correo electrónico
          </Button>
        )}
      </Space>
    </div>
  );
};

export const WrappedNormalLoginForm = Form.create<IFormComponentProps>({
  name: 'normal_login',
})(NormalLoginForm);
